/**
 * Chat Authentication Gate Component
 * Shows authentication prompt for non-authenticated users
 *
 * Accessibility:
 * - Clear, outcome-focused button labels
 * - Proper heading hierarchy
 * - Focus management on mount
 * - Respects reduced motion preferences
 */

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { LogIn, UserPlus, User, Info } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { useReducedMotion } from '../../../../shared/hooks/useReducedMotion';
import { useAuthOverlay } from '@/auth/AuthOverlayContext';

interface ChatAuthGateProps {
  onGuestContinue: () => void;
  onNavigate?: () => void;
  className?: string;
}

/**
 * Authentication gate for chat widget
 * Prompts users to login, register, or continue as guest
 */
export const ChatAuthGate: React.FC<ChatAuthGateProps> = ({
  onGuestContinue,
  onNavigate,
  className
}) => {
  const { t } = useTranslation('auth');
  const { prefersReducedMotion, tween } = useReducedMotion();
  const { openAuthSheet } = useAuthOverlay();
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus the container on mount for accessibility
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, []);

  /**
   * Handle login navigation with smooth UX
   */
  const handleLogin = (): void => {
    sessionStorage.setItem('amalq_return_to_chat', 'true');
    sessionStorage.setItem('amalq_chat_return_url', window.location.pathname);

    if (onNavigate) {
      onNavigate();
    }

    setTimeout(() => {
      openAuthSheet({
        returnTo: window.location.pathname,
        reason: t('multiMethod.reasonSaveChats')
      });
    }, 150);
  };

  /**
   * Handle signup navigation with smooth UX
   */
  const handleSignup = (): void => {
    sessionStorage.setItem('amalq_return_to_chat', 'true');
    sessionStorage.setItem('amalq_chat_return_url', window.location.pathname);

    if (onNavigate) {
      onNavigate();
    }

    setTimeout(() => {
      openAuthSheet({
        returnTo: window.location.pathname,
        reason: t('multiMethod.reasonCreateAccountChats')
      });
    }, 150);
  };

  // Shared button base styles
  const buttonBase = cn(
    'w-full flex items-center justify-center gap-3',
    'px-4 py-3.5 rounded-xl',
    'font-semibold text-base',
    'min-h-[48px]', // Minimum touch target
    'transition-colors duration-150',
    'focus:outline-hidden focus:ring-2 focus:ring-offset-2'
  );

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
      animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
      exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
      transition={tween}
      className={cn(
        'flex flex-col items-center justify-center p-6',
        'text-center',
        className
      )}
      role="region"
      aria-label="Authentication options"
    >
      <div className="w-full max-w-sm space-y-6">
        {/* Welcome Message */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-gray-900">
            Chat with Hajra
          </h2>
          <p className="text-base text-gray-600">
            Sign in to save your chats across devices
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3" role="group" aria-label="Sign in options">
          {/* Login Button */}
          <motion.button
            whileHover={prefersReducedMotion ? {} : { scale: 1.01 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.99 }}
            onClick={handleLogin}
            className={cn(
              buttonBase,
              'border-2 border-emerald-200 bg-white',
              'text-emerald-700',
              'hover:border-emerald-300 hover:bg-emerald-50',
              'focus:ring-emerald-500'
            )}
            type="button"
          >
            <LogIn className="h-5 w-5" aria-hidden="true" />
            <span>Sign in to save chats</span>
          </motion.button>

          {/* Sign Up Button - Primary CTA */}
          <motion.button
            whileHover={prefersReducedMotion ? {} : { scale: 1.01 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.99 }}
            onClick={handleSignup}
            className={cn(
              buttonBase,
              'bg-emerald-600 hover:bg-emerald-700',
              'text-white',
              'shadow-md',
              'focus:ring-emerald-500'
            )}
            type="button"
          >
            <UserPlus className="h-5 w-5" aria-hidden="true" />
            <span>Create account</span>
          </motion.button>

          {/* Divider */}
          <div className="relative py-2" role="separator">
            <div
              className="absolute inset-0 flex items-center"
              aria-hidden="true"
            >
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-gray-50 px-3 text-gray-500">or</span>
            </div>
          </div>

          {/* Guest Continue Button */}
          <motion.button
            whileHover={prefersReducedMotion ? {} : { scale: 1.01 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.99 }}
            onClick={onGuestContinue}
            className={cn(
              buttonBase,
              'border border-gray-200 bg-gray-50',
              'text-gray-700',
              'hover:border-gray-300 hover:bg-gray-100',
              'focus:ring-gray-500'
            )}
            type="button"
          >
            <User className="h-5 w-5" aria-hidden="true" />
            <span>Continue as guest</span>
          </motion.button>
        </div>

        {/* Guest Info Notice */}
        <motion.div
          initial={
            prefersReducedMotion ? { opacity: 1 } : { opacity: 0, height: 0 }
          }
          animate={{ opacity: 1, height: 'auto' }}
          transition={tween}
          className={cn(
            'rounded-xl p-3.5',
            'border border-blue-200 bg-blue-50',
            'flex items-start gap-3'
          )}
          role="note"
        >
          <Info
            className="mt-0.5 h-5 w-5 shrink-0 text-blue-600"
            aria-hidden="true"
          />
          <p className="text-left text-sm leading-relaxed text-blue-800">
            <strong className="font-semibold">
              Guest sessions are temporary.
            </strong>{' '}
            Your conversation will be cleared when you leave. Create an account
            to access your chats from any device.
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ChatAuthGate;
